import { message } from "antd";
import { DeliverablesFilterParams } from "../pages/DeliverableList/DeliverableList.types";
import { TemplateJsonResponse } from "../../../shared/screen-components/AgGridComponent/AgGridUtils";
import { ConditionalFormattingRule } from "../../../shared/screen-components/ExcelViewComponent/ExcelViewComponent.types";
import { FilterKeyTypes } from "../../../shared/types/FilterKeys.types";
import { SheetSort } from "../../../shared/screen-components/AgGridComponent/hooks/sheetSort";
import { ENV } from "../../../config";
import {
  DEFAULT_FILTER_KEYS,
  GLOBAL_CONSTANT,
} from "../../../shared/utils/CommonConstants";
import ApiService from "../../../core/services/ApiService";

const CLIENT_ID = ENV.CLIENT_ID;
export interface CreateDeliverablePayload {
  name: string;
  ruleEngineId: number;
  coaId?: number | null;
  tag?: { id: number; value: string }[];
}
class DeliverableService extends ApiService {
  basePath: string = "/api";

  async getAllFeatues<T>(userId: string, organizationId: string): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/user/feature?user-id=${userId}&organization-id=${organizationId}`,
      {
        method: "get",
        extractData: true,
      },
    );
  }

  async getAllDeliverables<T>(
    filterParams?: DeliverablesFilterParams | {},
  ): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/v2/deliverable?client-id=${CLIENT_ID}`,
      {
        method: "post",
        data: filterParams || ({} as any),
        extractData: true,
      },
    );
  }

  async deliverablesFilter<T>(): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/filter-options?client-id=${CLIENT_ID}`,
      {
        method: "get",
        extractData: true,
      },
    );
  }

  async getDeliverables<T>(filterParams: DeliverablesFilterParams): Promise<T> {
    const url = `${this.basePath}/deliverable`;
    const response = await this.makeRequest<T>(url, {
      method: "POST",
      data: filterParams as any,
      extractData: true,
    });
    return response;
  }

  async createDeliverable<T>(
    projectDetails: CreateDeliverablePayload,
  ): Promise<T> {
    return this.makeRequest<T>(`${this.basePath}/deliverable/create`, {
      method: "post",
      data: projectDetails as any,
      extractData: true,
    });
  }
  async getRefreshToken<T>(refreshToken: string): Promise<T> {
    const url = `${this.basePath}/auth/refresh-token`;
    const response = await this.makeRequest<T>(url, {
      method: "POST",
      data: { refreshToken } as any,
      extractData: true,
    });
    return response;
  }
  async getMultiSheetDetails<T>(id: number, sheetId: number) {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${id}/${sheetId}/multi-sheet-list`,
      {
        method: "get",
      },
    );
  }

  async getDeliverableMenuDetails<T>(id: number) {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${id}/menu-hierarchy`,
      {
        method: "get",
        extractData: true,
      },
    );
  }
  async getDeliverableDetailsById<T>(id: number) {
    return this.makeRequest<T>(`${this.basePath}/deliverable/${id}`, {
      method: "get",
      extractData: true,
    });
  }

  async updateDeliverableName<T>(id: number, name: { name: string }) {
    return this.makeRequest<T>(`${this.basePath}/deliverable/${id}`, {
      method: "put",
      data: name as any,
    });
  }

  async getProjectSheetsData<T>(
    deliverableId: number,
    sheetId: number,
    startIndex = 0,
    rowSize = 50,
    pageNumber?: string,
    filteredKeys: FilterKeyTypes = { ...DEFAULT_FILTER_KEYS },
  ) {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}?start-index=${
        startIndex > 0 ? startIndex : 0
      }&row-size=${rowSize}&sheet-number=${pageNumber ?? ""}&key1=${
        filteredKeys.Key1
      }&key2=${filteredKeys.Key2}&key3=${filteredKeys.Key3}&key4=${
        filteredKeys.Key4
      }`,
      { method: "get" },
    );
  }

  async getDeliverableTemplate<T>(id: number) {
    return this.makeRequest<T>(`${this.basePath}/deliverable/${id}/template`, {
      method: "get",
    });
  }

  async getSheetBlock(
    deliverableId: number,
    sheetId: number,
    startIndex: number,
    rowSize: number,
    sort?: SheetSort | null,
  ): Promise<TemplateJsonResponse | undefined> {
    return this.makeRequest<TemplateJsonResponse>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/template`,
      {
        params: {
          "is-json": true,
          paged: true,
          "start-index": startIndex,
          "row-size": rowSize,
          ...(sort ? { "sort-by": sort.field, "sort-direction": sort.direction } : {}),
        },
        extractData: true,
      },
    );
  }

  async saveSheetRows(
    deliverableId: string,
    sheetId: string,
    payload: { rows: { rowIndex: number | null; values: Record<string, unknown> }[]; deletedRowIndexes?: number[] },
    editSessionId?: string,
  ): Promise<void> {
    await this.makeRequest(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/rows`,
      {
        method: "post",
        data: payload,
        params: editSessionId ? { "edit-session-id": editSessionId } : undefined,
        extractData: true,
      },
    );
  }

  async getTemplateUsingAxiosWithJson(
    deliverableId: number,
    sheetId: number,
    multiSheet?: boolean,
    startIndex: number = 0,
    rowSize: number = 10,
    filteredKeys?: FilterKeyTypes,
    isJson: boolean = false,
  ): Promise<TemplateJsonResponse | undefined> {
    try {
      return this.makeRequest<TemplateJsonResponse>(
        `${this.basePath}/deliverable/${deliverableId}/${sheetId}/template`,
        {
          params: { "is-json": isJson },
          extractData: true,
        },
      );
    } catch (error) {
      message.error(GLOBAL_CONSTANT.TOAST_DEFAULT_ERROR_MESSAGE);
    }
  }

  async getTemplateUsingAxios(
    deliverableId: number,
    sheetId: number,
    multiSheet?: boolean,
    startIndex: number = 0,
    rowSize: number = 10,
    filteredKeys?: FilterKeyTypes,
    isJson: boolean = false,
  ): Promise<Blob | undefined> {
    try {
      return this.makeRequest<Blob>(
        `${this.basePath}/deliverable/${deliverableId}/${sheetId}/template`,
        {
          responseType: "blob",
          params: { "is-json": isJson },
          extractData: true,
        },
      );
    } catch (error) {
      message.error(GLOBAL_CONSTANT.TOAST_DEFAULT_ERROR_MESSAGE);
    }
  }

  async getDeliverableTemplateUsingAxios(
    deliverableId: number,
    isJson: boolean = false,
  ): Promise<Blob> {
    return this.makeRequest<Blob>(
      `${this.basePath}/deliverable/${deliverableId}/template`,
      {
        responseType: "blob",
        params: { "is-json": isJson },
        extractData: true,
      },
    );
  }

  async getConditionalFormattingUsingAxios(
    deliverableId: number,
    sheetId: number,
  ): Promise<ConditionalFormattingRule[]> {
    return this.makeRequest<ConditionalFormattingRule[]>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/template-conditional-format`,
      { extractData: true },
    );
  }
  async import<T>(
    id: string,
    sheetId: string,
    file: File,
    multiSheet?: boolean,
    startIndex: number = 0,
    isAgGridImport: boolean = false,
    editSessionId?: string,
  ): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("file", file);
    const editSession = editSessionId
      ? `&edit-session-id=${encodeURIComponent(editSessionId)}`
      : "";
    return this.makeRequest(
      `${this.basePath}/deliverable/${id}/${sheetId}/import?start-index=${
        startIndex < 1 ? 0 : startIndex
      }&ag-grid-import=${isAgGridImport}${editSession}`,
      {
        method: "post",
        data: formData,
        extractData: true,
      },
    );
  }

  async exportAxios<T>(
    id: string,
    sheetId: string,
    multiSheet?: boolean,
    filteredKeys: FilterKeyTypes = { ...DEFAULT_FILTER_KEYS },
    rowCount?: number,
    sheetNumber?: string,
  ): Promise<T> {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${id}/${sheetId}/export`,
      {
        responseType: "blob",
        params: {
          "multi-sheet": multiSheet ?? false,
          "keys.Key1": filteredKeys.Key1,
          "keys.Key2": filteredKeys.Key2,
          "keys.Key3": filteredKeys.Key3,
          "keys.Key4": filteredKeys.Key4,
          "add-default-row": rowCount,
        },
        extractData: true,
      },
    );
  }

  async updateProjectDetails<T>(id: number, data: any) {
    return this.makeRequest<T>(`${this.basePath}/deliverable/${id}`, {
      method: "put",
      data: data,
    });
  }

  async getKeySequence<T>(deliverableId: any, sheetId: any) {
    return this.makeRequest<T>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/key-sequence`,
    );
  }
  async taskStatus<T>(
    deliverableId: any,
    sheetId?: string,
    taskid?: number,
    taskType?: string,
  ): Promise<T | null> {
    try {
      return this.makeRequest<T>(
        `${this.basePath}/deliverable/${deliverableId}/task-status`,
        {
          params: {
            "sheet-id": sheetId ?? "",
            "task-id": taskid ?? "",
            "task-type": taskType ?? "",
          },
          extractData: true,
        },
      );
    } catch {
      return null;
    }
  }

  async getSheetEditLock(
    deliverableId: string,
    sheetId: string,
  ): Promise<SheetLockStatus> {
    return this.makeRequest<SheetLockStatus>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/edit-lock`,
      { method: "get", extractData: true },
    );
  }

  async acquireSheetEditLock(
    deliverableId: string,
    sheetId: string,
  ): Promise<SheetEditLock> {
    return this.makeRequest<SheetEditLock>(
      `${this.basePath}/deliverable/${deliverableId}/${sheetId}/edit-lock`,
      { method: "post", extractData: true },
    );
  }

  async renewSheetEditLock(sessionId: string): Promise<SheetEditLock> {
    return this.makeRequest<SheetEditLock>(
      `${this.basePath}/deliverable/edit-lock/${encodeURIComponent(sessionId)}`,
      { method: "put", extractData: true },
    );
  }

  async releaseSheetEditLock(sessionId: string): Promise<void> {
    await this.makeRequest(
      `${this.basePath}/deliverable/edit-lock/${encodeURIComponent(sessionId)}`,
      { method: "delete" },
    );
  }
}

export interface SheetLockStatus {
  isLocked: boolean;
  lockType: "edit" | "import" | null;
  lockedByUserId: string | null;
  lockedByName: string | null;
  lockedByCurrentUser: boolean;
  sessionId: string | null;
  expiresAt: string | null;
}

export interface SheetEditLock {
  sessionId: string;
  expiresAt: string;
  heartbeatIntervalSeconds: number;
  lockedSheetCount: number;
}

export default DeliverableService;
